import {createSelector} from '@reduxjs/toolkit';

import {type RootState} from 'store/store';
import {type TransientTimeFrameDraft} from 'pages/comp-pass/components/comp-pass-wizard/comp-pass-wizard.types';
import {transientTimeFrame} from 'pages/comp-pass/store/comp-pass-adapters';
import {selectCompPassType} from 'pages/comp-pass/store/comp-pass-selectors';
import {selectNumberOfSelectedEvents} from 'pages/comp-pass/store/event-selection-selectors';
import {PASS_TYPE} from 'pages/comp-pass/components/comp-pass-wizard/utils';

const adapterSelectors = transientTimeFrame.getSelectors(
    (state: RootState) => state.compPass.passCreation.transientTimeFrames
);

/**
 * Selector for all transient time-frame draft rows in insertion order.
 */
export const selectAllTransientTimeFrames = adapterSelectors.selectAll;

/**
 * Selector for the total number of transient time-frame draft rows
 * (filled or not).
 */
export const selectNumberOfTransientTimeFrames = adapterSelectors.selectTotal;

/**
 * Selector for the per-row, per-field error map produced by the validation
 * thunk, or `null` when there are no validation errors.
 */
export const selectTransientTimeFrameErrors = (state: RootState) =>
    state.compPass.passCreation.errors.transientTimeFrames;

/**
 * Selector for whether validation errors should be revealed on every transient
 * field (set when the user attempts to advance past an invalid form).
 */
export const selectDisplayAllTransientErrors = (state: RootState) =>
    state.compPass.passCreation.errors.displayAllTransientErrors;

const isRowFullyFilled = (row: TransientTimeFrameDraft) =>
    Boolean(
        row.label &&
            row.startDate &&
            row.startTime &&
            row.endDate &&
            row.endTime
    );

const isRowDirty = (row: TransientTimeFrameDraft) =>
    Boolean(
        row.label ||
            row.startDate ||
            row.startTime ||
            row.endDate ||
            row.endTime
    );

/**
 * Selector indicating whether the user has entered any transient data. True
 * when at least one row has at least one non-empty field. Used to gate the
 * pass-type toggle so an untouched (auto-added empty) row does not lock the
 * radio before the user has typed anything.
 */
export const selectHasTransientData = createSelector(
    selectAllTransientTimeFrames,
    (rows) => rows.some(isRowDirty)
);

/**
 * Selector for the count of "valid" transient time-frame rows for the
 * top-of-page counter. Per OCP-3644 AC #10a a row only counts when every
 * field is filled.
 */
export const selectNumberOfValidTransientTimeFrames = createSelector(
    selectAllTransientTimeFrames,
    (rows) => rows.filter(isRowFullyFilled).length
);

/**
 * Selector for the Step 1 selection count that drives the "X Selected" counter
 * and the total-passes multiplier: number of selected events for the event
 * flow, or number of valid transient time-frames for the transient flow.
 */
export const selectStep1SelectionCount = createSelector(
    selectCompPassType,
    selectNumberOfSelectedEvents,
    selectNumberOfValidTransientTimeFrames,
    (passType, numberOfEvents, numberOfValidTransientTimeFrames) =>
        passType === PASS_TYPE.TRANSIENT
            ? numberOfValidTransientTimeFrames
            : numberOfEvents
);
